import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Camera, Images } from "lucide-react";
import { api, formatError } from "../lib/api";
import { TalaveraStrip } from "../components/TalaveraStrip";

const NAME_KEY = "moments_name";

export default function MomentsPage() {
  const [name, setName] = useState(() => sessionStorage.getItem(NAME_KEY) || "");
  const [nameDraft, setNameDraft] = useState("");
  const [nameError, setNameError] = useState("");
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const saveName = (e) => {
    e.preventDefault();
    const clean = nameDraft.trim().replace(/\s+/g, " ");
    if (clean.length < 2) {
      setNameError("Escribe tu nombre para continuar");
      return;
    }
    sessionStorage.setItem(NAME_KEY, clean);
    setName(clean);
    setNameError("");
    toast.success(`¡Bienvenido, ${clean}!`);
  };

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("name", name);
        const { data } = await api.post("/moments", fd);
        setUploads((u) => [{ id: data.id, url: URL.createObjectURL(f), isVideo: f.type.startsWith("video/") }, ...u]);
        ok++;
      } catch (e) {
        toast.error(formatError(e, "No se pudo subir una foto"));
      }
    }
    if (ok > 0) {
      toast.success(ok === 1 ? "¡Compartido! Gracias" : `¡${ok} archivos compartidos! Gracias`);
    }
    setBusy(false);
    if (cameraRef.current) cameraRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
  };

  const changeName = () => {
    sessionStorage.removeItem(NAME_KEY);
    setName("");
    setNameDraft("");
  };

  return (
    <div data-testid="moments-page" className="min-h-screen bg-cream relative overflow-hidden">
      <div className="relative z-10 max-w-md mx-auto px-6 py-16">
        <TalaveraStrip count={3} className="mb-8" />
        <p className="font-script text-5xl text-clay text-center">Emma & Jhonny</p>
        <h1 className="font-serif text-3xl tracking-tight text-center mt-3 text-ink">Comparte tus momentos</h1>
        <p className="text-center text-stone text-sm mt-4 leading-relaxed">
          Toma fotos y videos con tu celular durante la boda y aparecerán al instante en la galería para todos los
          invitados.
        </p>

        {!name ? (
          <form data-testid="moments-name-form" onSubmit={saveName} className="mt-8 space-y-4">
            <label className="block text-[11px] uppercase tracking-[0.35em] text-stone text-center">Tu nombre</label>
            <input
              data-testid="moments-name-input"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              placeholder="Ej. María Pérez"
              maxLength={60}
              className="w-full bg-cream border border-clay/25 rounded-2xl px-6 py-4 text-center text-lg text-ink placeholder:text-stone/40 focus:outline-none focus:border-clay transition-colors"
            />
            {nameError && (
              <p data-testid="moments-name-error" className="text-center text-sm text-red-800">
                {nameError}
              </p>
            )}
            <button
              data-testid="moments-name-submit"
              type="submit"
              className="w-full rounded-full bg-clay text-cream py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-clay-deep transition-colors"
            >
              Continuar
            </button>
          </form>
        ) : (
          <div className="mt-10">
            <p className="font-script text-3xl text-clay text-center">Hola, {name}</p>
            <input
              ref={cameraRef}
              data-testid="moments-camera-input"
              type="file"
              accept="image/*,video/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <input
              ref={galleryRef}
              data-testid="moments-gallery-input"
              type="file"
              accept="image/*,video/*"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <div className="mt-8 space-y-4">
              <button
                data-testid="moments-take-photo-button"
                onClick={() => cameraRef.current?.click()}
                disabled={busy}
                className="w-full rounded-full bg-clay text-cream py-5 text-[12px] uppercase tracking-[0.3em] hover:bg-clay-deep transition-colors disabled:opacity-60 flex items-center justify-center gap-3"
              >
                <Camera size={18} /> {busy ? "Subiendo…" : "Tomar foto o video"}
              </button>
              <button
                data-testid="moments-pick-photo-button"
                onClick={() => galleryRef.current?.click()}
                disabled={busy}
                className="w-full rounded-full border border-clay text-clay py-5 text-[12px] uppercase tracking-[0.3em] hover:bg-clay hover:text-cream transition-colors disabled:opacity-60 flex items-center justify-center gap-3"
              >
                <Images size={18} /> Elegir de mi galería
              </button>
            </div>

            {uploads.length > 0 && (
              <div data-testid="moments-uploads-grid" className="grid grid-cols-3 gap-3 mt-10">
                {uploads.map((u) =>
                  u.isVideo ? (
                    <motion.video
                      key={u.id}
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4 }}
                      src={u.url}
                      controls
                      playsInline
                      muted
                      className="rounded-2xl aspect-square object-cover border border-clay/10"
                    />
                  ) : (
                    <motion.img
                      key={u.id}
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4 }}
                      src={u.url}
                      alt="Foto compartida"
                      className="rounded-2xl aspect-square object-cover border border-clay/10"
                    />
                  )
                )}
              </div>
            )}

            <a
              data-testid="moments-view-gallery"
              href="/#fotos"
              className="block text-center mt-10 text-[11px] uppercase tracking-[0.25em] text-clay hover:text-clay-deep transition-colors"
            >
              Ver la galería de la boda
            </a>
            <button
              data-testid="moments-change-name"
              onClick={changeName}
              className="mt-4 w-full text-center text-stone text-[11px] uppercase tracking-[0.25em] hover:text-clay transition-colors"
            >
              ¿No eres {name}? Cambiar nombre
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
